import { useMutation } from '@apollo/client'
import { Box, FormModal, IFormModalItem, Link, Text, VStack } from '@chaine/keychaine'
import React, { useEffect, useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import * as Yup from 'yup'

import { Toast } from '../../../_shared/components/toast'
import { AppModalProps } from '../components/apps-data'
import { ENABLE_US1_NETWORK_TMS_APP } from '../data/mutations'

interface IAppMetadata {
  tmsURL?: string
}

interface IAppCredentials {
  password?: string
  username?: string
}

interface IWorkspaceApp {
  appCredentials?: IAppCredentials
  appMetadata?: IAppMetadata
  appType?: string
}

type TFieldValues = {
  password: string
  tmsURL: string
  username: string
}

const validationSchema = Yup.object().shape({
  password: Yup.string().required('Password is required'),
  tmsURL: Yup.string().required('US1Network TMS URL is required'),
  username: Yup.string().required('Username is required')
})

/**
 * US1Network TMS Modal component that handles collecting and submitting credentials
 */
const US1NetworkTMSModal = React.memo((props: AppModalProps) => {
  const { isOpen, refetchApps, onClose, app, isModalEditable } = props
  const [formError, setFormError] = useState('')
  const { showToast } = Toast()

  useEffect(() => {
    if (app?.workspaceApp) {
      console.log('TMS App data:', app.workspaceApp)
      console.log('TMS URL value:', (app.workspaceApp as IWorkspaceApp)?.appMetadata?.tmsURL)
    }
  }, [app])

  // US1Network TMS app enable mutation
  const [enableUS1NetworkTMSApp, { loading: isEnabling }] = useMutation(ENABLE_US1_NETWORK_TMS_APP, {
    onCompleted: () => {
      showToast({
        status: 'success',
        title: 'US1Network TMS app enabled successfully!'
      })
      refetchApps()
      setFormError('')
      onClose()
    },
    onError: (err) => {
      setFormError(`${err.message}`)
      showToast({
        status: 'error',
        title: `${err.message}`
      })
    }
  })

  const handleCredentialsSubmit: SubmitHandler<TFieldValues> = (values) => {
    enableUS1NetworkTMSApp({
      variables: {
        password: values.password,
        tmsURL: values.tmsURL,
        username: values.username
      }
    })
  }

  const closeModal = () => {
    setFormError('')
    onClose()
  }

  const modalItems: IFormModalItem[] = isModalEditable
    ? [
        {
          inputType: 'text',
          isDisabled: true,
          labelText: 'Truckman API URL',
          name: 'tmsURL',
          placeHolder: 'i.e. https://truckman3.com',
          type: 'input',
          value: (app?.workspaceApp as IWorkspaceApp)?.appMetadata?.tmsURL
        },
        {
          inputType: 'text',
          isDisabled: true,
          labelText: 'Truckman API UserName',
          name: 'username',
          type: 'input',
          value: (app?.workspaceApp as IWorkspaceApp)?.appCredentials?.username
        },
        {
          inputType: 'password',
          isDisabled: true,
          labelText: 'Truckman API Password',
          name: 'password',
          type: 'input',
          value: (app?.workspaceApp as IWorkspaceApp)?.appCredentials?.password
        }
      ]
    : [
        {
          inputType: 'text',
          isRequired: true,
          labelText: 'Truckman API URL',
          name: 'tmsURL',
          placeHolder: 'i.e. https://truckman3.com',
          type: 'input'
        },
        {
          inputType: 'text',
          isRequired: true,
          labelText: 'Truckman API UserName',
          name: 'username',
          placeHolder: 'i.e. ChainApp',
          type: 'input'
        },
        {
          inputType: 'password',
          isRequired: true,
          labelText: 'Truckman API Password',
          name: 'password',
          placeHolder: 'i.e. S0meStr0ngP@ssw0rd',
          type: 'input'
        }
      ]

  return (
    <FormModal
      isOpen={isOpen}
      onClose={closeModal}
      size="lg"
      title={isModalEditable ? 'View US1Network TMS Credentials' : 'Enable US1Network TMS for Chain'}
      subtitle={
        isModalEditable
          ? 'View your US1Network TMS credentials below'
          : 'Enter your US1Network TMS credentials to enable the integration'
      }
      formSubmitHandler={handleCredentialsSubmit}
      buttonText={isModalEditable ? 'Close' : 'Enable'}
      validationSchema={validationSchema}
      isLoading={isEnabling}
      footer={
        formError ? (
          <Box mb={4}>
            <Text color="red.600">{formError}</Text>
          </Box>
        ) : undefined
      }
      modalBodyProps={{ maxHeight: 600, overflow: 'auto' }}
      modalItems={modalItems}
      hideSubmitButton={isModalEditable}
    >
      <VStack spacing={4} align="start">
        <Link color="blue.500" href="https://help.trychain.com/en" target="_blank" rel="noopener noreferrer">
          Read the integration guide
        </Link>
      </VStack>
    </FormModal>
  )
})

US1NetworkTMSModal.displayName = 'US1NetworkTMSModal'

export { US1NetworkTMSModal }
